// Step 4i (Ahmed 2 Nov, mockup EfVWkyDgKfAePx556MQVCT screen 4 and phone screen 2; A1, A3, A4): "This invoice also covers this PO". The invoice stays as it was confirmed and
// lines already on a PO cannot change here. A line not on any PO is matched to a line of this PO, starting on what the reader matched; the split shows before and after and the
// total does not move. Nothing saves until "Add to the invoice" (invoice.service addPoToInvoice). No approval; it shows on Ahmed's dashboard.
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Spin, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { addPoToInvoice, AddPoView, getAddPoView, getInvoicePhotoLink, InvoiceView } from '../../api';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { money } from '../receiving/shared';

const { Text } = Typography;
const MUTED = '#5f6672';
const CHIP: React.CSSProperties = { fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 600, fontSize: 12, color: '#1e6fe6', whiteSpace: 'nowrap' };
const cell: React.CSSProperties = { padding: '6px 6px' };
const r2 = (n: number) => Math.round(n * 100) / 100;
type Part = { poId: string; amount: number };

export const AddPoWindow: React.FC<{ invoiceId: number | null; poId: string; photoId?: number; onClose: () => void; onDone: (inv: InvoiceView) => void }> = ({ invoiceId, poId, photoId, onClose, onDone }) => {
  const compact = useCompactLayout();
  const [view, setView] = useState<AddPoView | null>(null);
  const [picks, setPicks] = useState<Record<number, number | 'none'>>({});
  const [thumb, setThumb] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setView(null); setThumb(null);
    if (!invoiceId) return undefined;
    let alive = true;
    getAddPoView(invoiceId, poId, photoId).then((v) => {
      if (!alive) return;
      setView(v);
      setPicks(Object.fromEntries(v.lines.filter((l) => l.movable).map((l) => [l.id, l.startOn ?? 'none'])));
      if (/^image\/(jpeg|png|webp|gif)/.test(v.invoice.fileMime || '')) getInvoicePhotoLink(invoiceId).then((u) => alive && setThumb(u)).catch(() => undefined);
    }).catch((e) => { message.error(e?.response?.data?.error || 'Could not open the invoice'); onClose(); });
    return () => { alive = false; };
  }, [invoiceId, poId, photoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const moving = useMemo(() => (view ? view.lines.filter((l) => l.movable && typeof picks[l.id] === 'number') : []), [view, picks]);
  const after = useMemo<Part[]>(() => {
    if (!view) return [];
    const on = view.parts.map((p) => ({ poHeaderId: p.poHeaderId, poId: p.poId, amount: p.amount }));
    moving.forEach((l) => {
      if (l.from === view.po.id) return;
      const from = on.find((p) => p.poHeaderId === l.from);
      if (from) from.amount = r2(from.amount - l.amount);
      const to = on.find((p) => p.poHeaderId === view.po.id);
      if (to) to.amount = r2(to.amount + l.amount); else on.push({ poHeaderId: view.po.id, poId: view.po.poId, amount: l.amount });
    });
    return on.filter((p) => Math.abs(p.amount) > 0.005);
  }, [view, moving]);

  const save = async () => {
    if (!view || !moving.length) return;
    setSaving(true);
    try {
      const res = await addPoToInvoice(view.invoice.id, { poId: view.po.poId, photoId, lines: moving.map((l) => ({ invoiceLineId: l.id, poLineId: picks[l.id] as number })) });
      message.success(`${view.po.poId} added to invoice ${view.invoice.number || '(no number)'}`);
      onDone(res);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not add this PO to the invoice');
    } finally {
      setSaving(false);
    }
  };

  const number = view?.invoice.number || '(no number)';
  const poLine = (id: number | 'none' | undefined) => view?.poLines.find((l) => l.id === id) || null;
  const picker = (l: AddPoView['lines'][number]) => view && (
    <Select size="small" style={{ width: '100%', minWidth: 170 }} popupMatchSelectWidth={false} showSearch optionFilterProp="search" optionLabelProp="chosen" aria-label={`Which line of ${view.po.poId}`}
      value={picks[l.id] ?? 'none'} onChange={(v) => setPicks((x) => ({ ...x, [l.id]: v }))}
      options={[
        { label: view.po.poId, options: view.poLines.map((p) => ({ value: p.id, label: `L${p.lineNo} · ${p.item}`, chosen: `${view.po.poId} L${p.lineNo} · ${p.item}`, search: `${view.po.poId} L${p.lineNo} ${p.item}` })) },
        { label: 'Other', options: [{ value: 'none', label: 'Leave it not on any PO', chosen: 'Leave it not on any PO', search: 'leave it not on any po' }] },
      ]} />
  );
  const sumOf = (list: Part[]) => r2(list.reduce((s, p) => s + p.amount, 0));
  const bar = (label: string, list: Part[], tick: boolean, was: boolean) => view && (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center', fontSize: 13, background: was ? '#f5f6f8' : '#f0f5ff', border: `1px solid ${was ? '#e3e6ec' : '#d6e0ff'}`, borderRadius: 8, padding: '8px 12px' }}>
      <span>{label}</span>
      {list.map((p, i) => <React.Fragment key={p.poId}>{i > 0 && <span>+</span>}<span><span style={CHIP}>{p.poId}</span> <b>{money(p.amount)}</b></span></React.Fragment>)}
      <span>=</span>
      <span><b>{money(sumOf(list))}</b>{tick && (Math.abs(sumOf(list) - view.invoice.total) <= 1 ? <Text type="success"> ✓ same as the invoice total</Text> : <Text type="warning"> the total says {money(view.invoice.total)}</Text>)}</span>
    </div>
  );
  const froms = view ? Array.from(new Set(moving.filter((l) => l.from !== view.po.id).map((l) => view.parts.find((p) => p.poHeaderId === l.from)?.poId).filter(Boolean))) : [];
  const movingSum = r2(moving.reduce((s, l) => s + l.amount, 0));
  const footnote = moving.length > 0 && (
    <div style={{ fontSize: 12.5, color: MUTED }}>The {money(movingSum)} {moving.every((l) => l.waiting) ? 'waiting for a reason ' : ''}{froms.length ? `on ${froms.join(', ')} ` : ''}closes and says where it went. This shows on Ahmed's dashboard under Price differences.</div>
  );

  let body: React.ReactNode = <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;
  if (view && compact) {
    // Mockup "On a phone": the lines already on a PO fold into one line; a line left off gets its own card with the list.
    const placed = view.lines.filter((l) => !l.movable);
    const placedOn = Array.from(new Set(placed.map((l) => l.onPoId).filter(Boolean)));
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {placed.length > 0 && <div style={{ border: '1px solid #e3e6ec', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, color: MUTED }}>{placed.length} line{placed.length === 1 ? '' : 's'} already on {placedOn.join(' and ')}</div>}
        {view.lines.filter((l) => l.movable).map((l) => {
          const pl = poLine(picks[l.id]);
          return (
            <div key={l.id} style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontWeight: 600 }}><span dir="auto">{l.description}</span><span>{money(l.amount)}</span></div>
              <div style={{ fontSize: 12, color: MUTED }}>{l.qty ?? ''}{pl?.unit ? ` ${pl.unit}` : ''} × {l.unitPrice != null ? money(l.unitPrice) : ''}{pl ? ` · PO price ${money(pl.price)}` : ''}</div>
              <div style={{ marginTop: 6 }}>{picker(l)}</div>
            </div>
          );
        })}
        <div style={{ border: '1px solid #e3e6ec', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
          <b>After</b>
          {after.map((p) => <div key={p.poId}>{p.poId} {money(p.amount)}</div>)}
          {Math.abs(sumOf(after) - view.invoice.total) <= 1 ? <Text type="success">= {money(sumOf(after))} ✓</Text> : <Text type="warning">= {money(sumOf(after))}, the total says {money(view.invoice.total)}</Text>}
        </div>
        {footnote}
      </div>
    );
  } else if (view) {
    body = (
      <div style={{ display: 'flex', gap: 14 }}>
        <div style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {thumb
            ? <a href={thumb} target="_blank" rel="noopener noreferrer"><img src={thumb} alt={view.invoice.fileName || 'invoice'} style={{ width: 150, maxHeight: 200, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', display: 'block' }} /></a>
            : <div style={{ width: 150, height: 90, borderRadius: 6, border: '1px dashed #d9d9d9', display: 'grid', placeItems: 'center', fontSize: 12, color: MUTED }}>{view.invoice.fileName || 'photo'}</div>}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13.5 }}>Invoice <b>{number}</b>{view.invoice.date ? ` · ${dayjs(view.invoice.date).format('D MMM YYYY')}` : ''} · EGP {money(view.invoice.total)}{view.invoice.by ? ` · confirmed by ${view.invoice.by}` : ''}</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                <th style={cell}>Invoice line</th><th style={cell}>Matched to PO line</th><th style={{ ...cell, textAlign: 'right' }}>Qty</th><th style={{ ...cell, textAlign: 'right' }}>Price</th><th style={{ ...cell, textAlign: 'right' }}>PO price</th>
              </tr></thead>
              <tbody>
                {view.lines.map((l) => {
                  const pl = l.movable ? poLine(picks[l.id]) : null;
                  const poPrice = l.movable ? pl?.price ?? null : l.poPrice;
                  const differs = l.kind === 'item' && poPrice != null && l.unitPrice != null && Math.abs(l.unitPrice - poPrice) > 0.005;
                  return (
                    <tr key={l.id} style={{ borderTop: '1px solid #f0f0f0', ...(l.movable ? { background: '#fffbe6' } : { color: '#8a94a6' }) }}>
                      <td style={cell} dir="auto">{l.description}</td>
                      <td style={{ ...cell, minWidth: 200 }}>
                        {l.movable ? picker(l)
                          : l.kind !== 'item' ? <span>a {l.kind}{l.onPoId ? <> on <span style={CHIP}>{l.onPoId}</span></> : null}</span>
                            : l.onLineNo ? <span style={CHIP}>{l.onPoId} L{l.onLineNo}</span>
                              : <span>not on any PO · explained as a charge</span>}
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>{l.qty ?? ''}</td>
                      <td style={{ ...cell, textAlign: 'right', ...(differs && l.movable ? { background: '#fff1f0', color: '#cf1322', fontWeight: 600 } : {}) }}>{l.kind === 'item' ? (l.unitPrice != null ? money(l.unitPrice) : '') : money(l.amount)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{poPrice != null ? money(poPrice) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {bar('Now:', view.parts, false, true)}
          {bar('After:', after, true, false)}
          {footnote}
        </div>
      </div>
    );
  }

  return (
    <Modal
      open={!!invoiceId}
      title={<div style={{ paddingInlineEnd: 28 }}>{compact ? `Add ${poId} to ${number}` : <>Add {poId} to invoice {number}{view?.po.vendor ? <> · <bdi>{view.po.vendor}</bdi></> : null}</>}{compact && view ? <Text type="secondary" style={{ float: 'right', fontWeight: 400 }}>{money(view.invoice.total)}</Text> : null}</div>}
      onCancel={onClose}
      onOk={save}
      okText="Add to the invoice"
      okButtonProps={{ disabled: !moving.length, loading: saving }}
      maskClosable={false}
      width={compact ? 520 : 940}
      destroyOnClose
    >
      {body}
    </Modal>
  );
};
